import React, { useCallback, useContext, useMemo, useRef, useState, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Dropdown from 'react-bootstrap/Dropdown';
import Modal from 'react-bootstrap/Modal';
import { AppThemeContext } from '../../context/AppThemeContext';
import { setPageDirty } from '../../utils/pageLeaveGuard';
import './GridPage.css';
// import { exportGridToWebpDataUrl } from '../../utils/exportGridToWebp';

function readFilesAsImages(files) {
  const imagePromises = Array.from(files)
    .filter(f => (
      f.type === 'image/png' ||
      f.type === 'image/webp' ||
      f.type === 'image/jpeg' ||
      f.name.toLowerCase().endsWith('.png') ||
      f.name.toLowerCase().endsWith('.jpg') ||
      f.name.toLowerCase().endsWith('.jpeg')
    ))
    .map(file => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => resolve({ img, name: file.name });
        img.onerror = reject;
        img.src = reader.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    }));
  return Promise.all(imagePromises);
}

export default function GridPage({ onExport }) {
  const { theme } = useContext(AppThemeContext);
  const [drawerImages, setDrawerImages] = useState([]); // {img, name}
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  const [cellSize, setCellSize] = useState(745);
  const [aspect, setAspect] = useState('tcg'); // '1:1' | '3:4' | 'tcg'
  const [exportScale, setExportScale] = useState(1); // .25x, .5x, 1x, 2x for export
  const [maxCellSize, setMaxCellSize] = useState(745); // Base TCG card size
  const [placed, setPlaced] = useState({}); // key: `${r}-${c}` -> {src}
  const [counts, setCounts] = useState({}); // src -> quantity placed
  const [isDrawerDragOver, setIsDrawerDragOver] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showAutoScaleModal, setShowAutoScaleModal] = useState(false);
  const [pendingAutoScale, setPendingAutoScale] = useState(null);
  const [exportBgTransparent, setExportBgTransparent] = useState(false);
  const [exportProgress, setExportProgress] = useState({ active: false, step: '', percent: 0 });
  const dragRef = useRef(null);
  const boardRef = useRef(null);
  const fitOuterRef = useRef(null);
  const [scale, setScale] = useState(1);

  const cellWH = useMemo(() => {
    if (aspect === '1:1') return { w: cellSize, h: cellSize };
    if (aspect === 'tcg') return { w: cellSize, h: Math.round(cellSize * 1040 / 745) };
    return { w: cellSize, h: Math.round(cellSize * 4 / 3) };
  }, [cellSize, aspect]);

  const boardStyle = useMemo(() => ({
    width: cols * cellWH.w,
    height: rows * cellWH.h
  }), [cols, rows, cellWH]);

  // Fit board to available wrapper size - always scale to fit
  const fitToContainer = useCallback(() => {
    if (!fitOuterRef.current) return;
    // Measure the actual canvas viewport (the immediate wrapper around the fit container)
    const viewport = fitOuterRef.current.parentElement; // .grid-canvas-wrapper
    if (!viewport) return;
    const vpRect = viewport.getBoundingClientRect();
    const bw = cols * cellWH.w;
    const bh = rows * cellWH.h;
    if (bw === 0 || bh === 0) return;
    // Fit by available viewport width/height
    const sx = vpRect.width / bw;
    const sy = vpRect.height / bh;
    const s = Math.min(sx, sy);
    setScale(s > 0 ? s : 1);
  }, [cols, rows, cellWH]);

  useEffect(() => {
    const imageCount = drawerImages.length;
    const placedCount = Object.keys(placed).length;
    const dirty = imageCount > 0 || placedCount > 0;
    setPageDirty(
      'grid',
      dirty,
      dirty ? `Grid images (${imageCount} in drawer, ${placedCount} placed) stay in memory and will be lost.` : ''
    );
    return () => setPageDirty('grid', false);
  }, [drawerImages, placed]);

  useEffect(() => {
    fitToContainer();
  }, [fitToContainer, placed]);

  useEffect(() => {
    const onResize = () => fitToContainer();
    window.addEventListener('resize', onResize);
    // Observe the canvas viewport for size changes
    const viewport = fitOuterRef.current ? fitOuterRef.current.parentElement : null;
    let ro;
    if (viewport && 'ResizeObserver' in window) {
      ro = new ResizeObserver(() => {
        fitToContainer();
      });
      ro.observe(viewport);
    }
    return () => {
      window.removeEventListener('resize', onResize);
      if (ro) ro.disconnect();
    };
  }, [fitToContainer]);

  const getNextAvailableCell = useCallback(() => {
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const key = `${r}-${c}`;
        if (!placed[key]) return { r, c };
      }
    }
    return null;
  }, [rows, cols, placed]);

  const compactPlaced = useCallback((currentPlaced) => {
    const sources = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const key = `${r}-${c}`;
        if (currentPlaced[key]?.src) sources.push(currentPlaced[key].src);
      }
    }
    const newPlaced = {};
    let index = 0;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (index >= sources.length) break;
        const key = `${r}-${c}`;
        newPlaced[key] = { src: sources[index++] };
      }
      if (index >= sources.length) break;
    }
    return newPlaced;
  }, [rows, cols]);

  const placeAtNextCell = useCallback((src) => {
    const spot = getNextAvailableCell();
    if (!spot) return false;
    const key = `${spot.r}-${spot.c}`;
    setPlaced(prev => ({ ...prev, [key]: { src } }));
    setCounts(prev => ({ ...prev, [src]: (prev[src] || 0) + 1 }));
    return true;
  }, [getNextAvailableCell]);

  const handleDropzone = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer?.files || e.target.files;
    if (!files || files.length === 0) return;
    setIsLoading(true);
    const images = await readFilesAsImages(files).catch(() => []);
    // Update drawer list and stats
    setDrawerImages(prev => {
      const next = [...prev, ...images];
      const totalW = next.reduce((sum, x) => sum + (x.img.naturalWidth || x.img.width || 0), 0);
      const totalH = next.reduce((sum, x) => sum + (x.img.naturalHeight || x.img.height || 0), 0);
      const avgW = totalW / next.length || 0;
      const suggestedMax = avgW > 0 ? Math.min(avgW, 2048) : 745;
      setMaxCellSize(suggestedMax);
      return next;
    });

    // Batch place all images atomically to avoid race conditions
    const newSrcs = images.map(i => i.img.src);
    setPlaced(prev => {
      const next = { ...prev };
      // Find first empty cell position
      let startR = 0;
      let startC = 0;
      outer: for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          if (!next[`${r}-${c}`]) { startR = r; startC = c; break outer; }
        }
      }
      let r = startR, c = startC;
      const advance = () => { c++; if (c >= cols) { c = 0; r++; } };
      for (const src of newSrcs) {
        while (r < rows && next[`${r}-${c}`]) advance();
        if (r >= rows) break;
        next[`${r}-${c}`] = { src };
        advance();
      }
      return next;
    });

    // Update counts in one go
    setCounts(prev => {
      const updated = { ...prev };
      for (const { img } of images) {
        updated[img.src] = (updated[img.src] || 0) + 1;
      }
      return updated;
    });
    setIsDrawerDragOver(false);
    setIsLoading(false);
  }, [placeAtNextCell]);

  const handleDrawerDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDrawerDragOver(true);
  }, []);

  const handleDrawerDragLeave = useCallback((e) => {
    // Only reset when actually leaving the drawer
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDrawerDragOver(false);
  }, []);

  const handleDragStart = useCallback((src) => (e) => {
    dragRef.current = src;
    e.dataTransfer.effectAllowed = 'copy';
  }, []);

  const handleBoardDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleBoardDrop = useCallback((e) => {
    e.preventDefault();
    const rect = boardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;
    const c = Math.floor(x / cellWH.w);
    const r = Math.floor(y / cellWH.h);
    if (r >= 0 && r < rows && c >= 0 && c < cols && dragRef.current) {
      const key = `${r}-${c}`;
      const src = dragRef.current;
      setPlaced(prev => ({ ...prev, [key]: { src } }));
      setCounts(prev => ({ ...prev, [src]: (prev[src] || 0) + 1 }));
    }
  }, [cellWH, rows, cols, scale]);

  const gridCells = useMemo(() => {
    const cells = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const key = `${r}-${c}`;
        const left = c * cellWH.w;
        const top = r * cellWH.h;
        const item = placed[key];
        cells.push(
          <div
            key={key}
            className="grid-cell"
            style={{ left, top, width: cellWH.w, height: cellWH.h }}
          >
            {item && <img className="grid-item" src={item.src} alt={key} draggable={false} />}
          </div>
        );
      }
    }
    return cells;
  }, [rows, cols, cellWH, placed]);

  // (Preview removed; full right side is the canvas)

  const handleExport = useCallback(async () => {
    if (!onExport) return;
    setExportProgress({ active: true, step: 'Preparing canvas...', percent: 5 });
    
    const exportData = {
      rows,
      cols,
      cellSize: cellWH.w * exportScale,
      placed,
      aspect,
      transparent: exportBgTransparent,
    };
    
    setExportProgress({ active: true, step: 'Rendering images...', percent: 20 });
    
    try {
      await onExport(exportData, (progress) => {
        setExportProgress(progress);
      });
      setExportProgress({ active: true, step: 'Compressing...', percent: 85 });
      // Small delay to show compression step
      await new Promise(resolve => setTimeout(resolve, 300));
      setExportProgress({ active: true, step: 'Done!', percent: 100 });
      setTimeout(() => {
        setExportProgress({ active: false, step: '', percent: 0 });
      }, 800);
    } catch (error) {
      setExportProgress({ active: false, step: '', percent: 0 });
      console.error('Export failed:', error);
    }
  }, [rows, cols, cellWH, placed, aspect, exportScale, exportBgTransparent, onExport]);

  return (
    <div className="grid-page-container">
      <aside
        className={`grid-drawer ${theme === 'dark' ? 'dark-theme' : ''} ${isDrawerDragOver ? 'drag-over' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setIsDrawerDragOver(true); }}
        onDragEnter={handleDrawerDragEnter}
        onDragLeave={handleDrawerDragLeave}
        onDrop={handleDropzone}
      >
        <div className="dropzone">Drag PNGs here</div>
        <Form.Group className="mb-2">
          <Form.Label>Or upload</Form.Label>
          <Form.Control type="file" accept="image/png,image/webp" multiple onChange={handleDropzone} />
        </Form.Group>
        <div className="drawer-images">
          {drawerImages.map((d, i) => (
            <div key={i} className="thumb-wrap">
              <img
                className="drawer-thumb"
                src={d.img.src}
                alt={d.name}
                draggable
                onDragStart={handleDragStart(d.img.src)}
                onDoubleClick={() => placeAtNextCell(d.img.src)}
              />
              <div className="thumb-actions">
                <button className="thumb-btn" onClick={() => placeAtNextCell(d.img.src)}>+</button>
                <button className="thumb-btn" onClick={() => {
                  // remove one instance from the grid
                  const src = d.img.src;
                  setPlaced(prev => {
                    const entries = Object.entries(prev);
                    const idx = entries.findIndex(([key, val]) => val.src === src);
                    if (idx === -1) return prev;
                    const toRemoveKey = entries[idx][0];
                    const next = { ...prev };
                    delete next[toRemoveKey];
                    return compactPlaced(next);
                  });
                  setCounts(prev => ({ ...prev, [d.img.src]: Math.max(0, (prev[d.img.src] || 0) - 1) }));
                }}>-</button>
              </div>
              {!!counts[d.img.src] && (
                <span className="drawer-thumb-badge">{counts[d.img.src]}</span>
              )}
            </div>
          ))}
        </div>
      </aside>
      <main className="grid-workspace">
        <div className="grid-toolbar">
          <Button
            variant={theme === 'dark' ? 'outline-light' : 'outline-secondary'}
            size="sm"
            className="header-action-btn"
            onClick={() => window.history.back()}
          >
            ← Back
          </Button>
          <Form.Group className="grid-toolbar-group">
            <Form.Label className="m-0">Export Scale</Form.Label>
            <Dropdown onSelect={(k) => setExportScale(parseFloat(k))}>
              <Dropdown.Toggle variant="secondary" size="sm">
                {exportScale}x
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item eventKey="0.25">Small (0.25x)</Dropdown.Item>
                <Dropdown.Item eventKey="0.5">Medium (0.5x)</Dropdown.Item>
                <Dropdown.Item eventKey="1">Large (1x)</Dropdown.Item>
                <Dropdown.Item eventKey="2">XL (2x)</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>
          <Form.Group className="grid-toolbar-group">
            <Form.Label className="m-0">Rows</Form.Label>
            <Form.Control style={{ width: 80 }} type="number" min={1} max={12} value={rows} onChange={(e) => setRows(parseInt(e.target.value || '1', 10))} />
          </Form.Group>
          <Form.Group className="grid-toolbar-group">
            <Form.Label className="m-0">Cols</Form.Label>
            <Form.Control style={{ width: 80 }} type="number" min={1} max={12} value={cols} onChange={(e) => setCols(parseInt(e.target.value || '1', 10))} />
          </Form.Group>
          <Form.Group className="grid-toolbar-group">
            <Form.Label className="m-0">Aspect</Form.Label>
            <Form.Select style={{ width: 140 }} value={aspect} onChange={(e) => setAspect(e.target.value)}>
              <option value="tcg">TCG (745×1040)</option>
              <option value="3:4">3:4</option>
              <option value="1:1">1:1</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="grid-toolbar-group">
            <Form.Label className="m-0" style={{ fontSize: '0.85rem' }}>
              Export: {Math.round(cellWH.w * exportScale * cols)}×{Math.round(cellWH.h * exportScale * rows)}px
            </Form.Label>
          </Form.Group>
          <Form.Group className="grid-toolbar-group">
            <Form.Check
              type="checkbox"
              checked={exportBgTransparent}
              onChange={(e) => setExportBgTransparent(e.target.checked)}
              label="Transparent"
            />
          </Form.Group>
          <Button variant="primary" onClick={handleExport} disabled={exportProgress.active}>Export WEBP</Button>
        </div>
        <div className="grid-canvas-wrapper">
          <div ref={fitOuterRef} className="grid-fit-outer">
            <div
              className="grid-fit-inner"
              style={{ width: boardStyle.width, height: boardStyle.height, transform: `scale(${scale})` }}
            >
              {isLoading && (
                <div className="loading-overlay">
                  <div className="loading-box">Loading images…</div>
                </div>
              )}
              {exportProgress.active && (
                <div className="loading-overlay">
                  <div className="loading-box">
                    <div>{exportProgress.step}</div>
                    <div style={{ marginTop: 8, fontSize: '0.85rem' }}>{exportProgress.percent}%</div>
                  </div>
                </div>
              )}
              <div
                ref={boardRef}
                className={`grid-board ${aspect === '1:1' ? 'square-preview' : ''}`}
                style={boardStyle}
                onDragOver={handleBoardDragOver}
                onDrop={handleBoardDrop}
              >
                {gridCells}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}


